/**
 * Direct-ad product pricing. This module never writes to the Online Store cart,
 * intercepts purchase submission, or publishes analytics. A tokenless Storefront
 * cart is used only as an isolated price quote; its id and checkout URL are not
 * requested, retained, or used to replace the buyer's real cart.
 */
(() => {
  'use strict';

  const core = window.StarmistProductOfferCore;
  if (!core) return;

  const QUOTE_TTL_MS = 60_000;
  const QUOTE_TIMEOUT_MS = 8_000;
  const quoteCache = new Map();
  const quoteRequests = new Map();
  const refreshProducts = new WeakMap();
  const PRODUCT_SELECTOR = 'starmist-product[data-product-offer-product-id]';
  const QUOTE_QUERY = `mutation ProductOfferQuote($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        discountCodes { code applicable }
        lines(first: 2) {
          nodes {
            quantity
            merchandise { ... on ProductVariant { id } }
            cost {
              subtotalAmount { amount currencyCode }
              totalAmount { amount currencyCode }
            }
            discountAllocations {
              __typename
              discountedAmount { amount currencyCode }
              ... on CartCodeDiscountAllocation { code }
            }
          }
        }
      }
      userErrors { field message }
      warnings { code message }
    }
  }`;

  const parseJson = (value) => {
    try { return JSON.parse(value); } catch { return null; }
  };

  const readSelections = () => {
    try { return parseJson(window.sessionStorage.getItem(core.STORAGE_KEY)); } catch { return null; }
  };

  const saveSelections = (record) => {
    try {
      const previous = window.sessionStorage.getItem(core.STORAGE_KEY);
      if (!record.selections.length) {
        if (previous !== null) window.sessionStorage.removeItem(core.STORAGE_KEY);
        return;
      }
      const next = JSON.stringify(record);
      if (previous !== next) window.sessionStorage.setItem(core.STORAGE_KEY, next);
    } catch {
      // The native line property remains available when browser storage is blocked.
    }
  };

  // Shopify's Liquid prices use hundredths even for currencies without decimals.
  // Parse Storefront decimal strings without floating-point multiplication.
  const moneyCents = (money, currency) => {
    if (!money || money.currencyCode !== currency || typeof money.amount !== 'string') return null;
    const match = money.amount.match(/^(\d+)(?:\.(\d+))?$/);
    if (!match || /[1-9]/.test((match[2] || '').slice(2))) return null;
    const cents = Number(match[1]) * 100 + Number((match[2] || '').padEnd(2, '0').slice(0, 2));
    return Number.isSafeInteger(cents) ? cents : null;
  };

  const validateQuote = (response, context) => {
    const { variantId, code, currency, nativePriceCents, targetPriceCents } = context;
    const result = response?.data?.cartCreate;
    if (response?.errors?.length || !result?.cart || result.userErrors?.length || result.warnings?.length) {
      throw new Error('quote-rejected');
    }
    const applied = result.cart.discountCodes?.find((entry) => entry.code?.toUpperCase() === code.toUpperCase());
    if (applied?.applicable !== true) throw new Error('quote-code-unavailable');
    const lines = result.cart.lines?.nodes;
    if (!Array.isArray(lines) || lines.length !== 1) throw new Error('quote-line-mismatch');
    const line = lines[0];
    if (line.quantity !== 1 || line.merchandise?.id !== `gid://shopify/ProductVariant/${variantId}`) {
      throw new Error('quote-variant-mismatch');
    }
    const base = moneyCents(line.cost?.subtotalAmount, currency);
    const total = moneyCents(line.cost?.totalAmount, currency);
    if (base !== nativePriceCents || total === null || total >= base) throw new Error('quote-price-mismatch');
    let discount = 0;
    if (!Array.isArray(line.discountAllocations) || !line.discountAllocations.length) {
      throw new Error('quote-allocation-missing');
    }
    for (const allocation of line.discountAllocations) {
      const amount = moneyCents(allocation.discountedAmount, currency);
      if (allocation.__typename !== 'CartCodeDiscountAllocation' ||
        allocation.code?.toUpperCase() !== code.toUpperCase() || amount === null) {
        throw new Error('quote-allocation-mismatch');
      }
      discount += amount;
    }
    if (!Number.isSafeInteger(discount) || discount <= 0 || base - discount !== total ||
      (currency === 'USD' && total !== targetPriceCents)) throw new Error('quote-discount-mismatch');
    return Object.freeze({ priceCents: total, regularPriceCents: base, currency });
  };

  const getQuote = (context, forceRefresh = false) => {
    const key = JSON.stringify(context);
    const cached = quoteCache.get(key);
    if (!forceRefresh && cached && Date.now() - cached.at < QUOTE_TTL_MS) return Promise.resolve(cached.value);
    if (!forceRefresh && quoteRequests.has(key)) return quoteRequests.get(key);
    if (forceRefresh) quoteCache.delete(key);
    const request = (async () => {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), QUOTE_TIMEOUT_MS);
      try {
        const response = await window.fetch('/api/2026-10/graphql.json', {
          method: 'POST',
          credentials: 'omit',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
          body: JSON.stringify({
            query: QUOTE_QUERY,
            variables: {
              input: {
                buyerIdentity: { countryCode: context.country },
                lines: [{ merchandiseId: `gid://shopify/ProductVariant/${context.variantId}`, quantity: 1 }],
                discountCodes: [context.code],
              },
            },
          }),
        });
        if (!response.ok) throw new Error('quote-network-error');
        const value = validateQuote(await response.json(), context);
        if (quoteRequests.get(key) === request) quoteCache.set(key, { at: Date.now(), value });
        return value;
      } finally {
        window.clearTimeout(timeout);
      }
    })();
    quoteRequests.set(key, request);
    request.finally(() => {
      if (quoteRequests.get(key) === request) quoteRequests.delete(key);
    }).catch(() => {});
    return request;
  };

  const setupProduct = (product, config, rawConfig) => {
    if (product.dataset.productOfferInitialized === 'true') return;
    const productId = product.dataset.productOfferProductId;
    const variants = parseJson(product.querySelector('[data-product-variants]')?.textContent);
    if (!Array.isArray(variants)) return;
    const currency = product.dataset.currency || rawConfig.presentmentCurrency;
    const country = product.dataset.productOfferCountry || rawConfig.country;
    const marketId = typeof rawConfig.marketId === 'string' ? rawConfig.marketId : '';
    if (!/^[A-Z]{3}$/.test(currency || '') || !/^[A-Z]{2}$/.test(country || '')) return;
    product.dataset.productOfferInitialized = 'true';

    const forms = Array.from(product.querySelectorAll('form.product__form, form.product-sticky-purchase__form'));
    const prices = product.querySelectorAll('[data-product-price], [data-sticky-price]');
    const comparePrices = product.querySelectorAll('[data-product-compare-price]');
    const messages = product.querySelectorAll('[data-product-offer-message]');
    const checkoutNotes = product.querySelectorAll('[data-product-offer-checkout-note]');
    const accelerated = product.querySelectorAll('[data-product-offer-accelerated]');
    const state = { active: false, status: 'inactive', offer: null, rule: null, variant: null, priceCents: null };
    let generation = 0;
    let priceOverridden = false;

    let selectionError = '';
    const refreshSelection = () => {
      const selection = core.selectProductOffer(config, {
        productId,
        search: window.location.search,
        storedSelections: readSelections(),
        marketId,
        now: Date.now(),
      });
      saveSelections(selection.record);
      state.offer = selection.offer;
      selectionError = selection.source === 'url' && !selection.ok ? selection.reason : '';
    };
    refreshSelection();

    const formatMoney = (cents) => {
      if (typeof product.formatMoney === 'function') return product.formatMoney(cents);
      return `${new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency,
      }).format(cents / 100)} ${currency}`;
    };

    const restorePrice = () => {
      if (!priceOverridden || !state.variant) return;
      prices.forEach((element) => { element.textContent = formatMoney(state.variant.price); });
      comparePrices.forEach((element) => {
        const compare = state.variant.compare_at_price;
        element.textContent = compare ? formatMoney(compare) : '';
        element.hidden = !compare || compare <= state.variant.price;
      });
      priceOverridden = false;
      state.priceCents = null;
    };

    const renderPrice = (cents) => {
      prices.forEach((element) => { element.textContent = formatMoney(cents); });
      comparePrices.forEach((element) => {
        element.textContent = formatMoney(state.variant.price);
        element.hidden = false;
      });
      priceOverridden = true;
      state.priceCents = cents;
    };

    const renderState = (message = '') => {
      product.dataset.productOfferActive = String(state.active);
      product.dataset.productOfferKey = state.offer?.key || '';
      product.dataset.productOfferCode = state.active ? state.rule.code : '';
      product.dataset.productOfferStatus = state.status;
      forms.forEach((form) => {
        const field = form.querySelector('[data-product-offer-property]');
        if (field) {
          field.disabled = !state.active;
          field.value = state.active ? state.offer.key : '';
        }
      });
      messages.forEach((element) => { element.textContent = message; element.hidden = !message; });
      accelerated.forEach((element) => { element.hidden = state.active; });
      checkoutNotes.forEach((element) => { element.hidden = !state.active; });
      product.dispatchEvent(new CustomEvent('starmist:product-offer-change', {
        bubbles: true,
        detail: {
          productId, variantId: state.variant ? String(state.variant.id) : '',
          offerKey: state.offer?.key || '', code: state.active ? state.rule.code : '',
          active: state.active, status: state.status, priceCents: state.priceCents, currency,
        },
      }));
    };

    const eligibility = (variant) => {
      if (!state.offer || !variant) return { ok: false, reason: selectionError || 'inactive' };
      const resolved = core.resolveOffer(config, {
        offerKey: state.offer.key, productId, variantId: String(variant.id), now: Date.now(),
        marketId,
      });
      if (!resolved.ok) return resolved;
      if (variant.available === false) return { ok: false, reason: 'sold-out' };
      if (product.querySelector('[data-selling-plan-select]')?.value) {
        return { ok: false, reason: 'unsupported-selling-plan' };
      }
      if (!Number.isSafeInteger(variant.price) || variant.price <= 0 ||
        (currency === 'USD' && variant.price !== resolved.variant.basePriceCents)) {
        return { ok: false, reason: 'base-price-changed' };
      }
      return resolved;
    };

    const applyVariant = (variant, forceQuote = false) => {
      const requestGeneration = ++generation;
      state.variant = variant;
      restorePrice();
      state.rule = null;
      state.priceCents = null;
      const resolved = eligibility(variant);
      state.active = resolved.ok;
      state.status = resolved.ok ? 'quoting' : resolved.reason;
      if (!resolved.ok) {
        let message = '';
        if (resolved.reason === 'base-price-changed') {
          message = 'This offer has changed. The current product price is shown.';
        } else if (resolved.reason === 'unsupported-selling-plan') {
          message = 'This ad offer is available for one-time purchases only.';
        } else if (resolved.reason === 'wrong-market') {
          message = 'This ad offer is not available in your selected country. The regular product price is shown.';
        } else if (selectionError || ['disabled-offer', 'expired-offer', 'not-started'].includes(resolved.reason)) {
          message = 'This ad offer is currently unavailable for this product. The regular price is shown.';
        }
        renderState(message);
        return;
      }
      state.rule = resolved.variant;
      if (currency === 'USD') renderPrice(state.rule.targetPriceCents);
      renderState(currency === 'USD' ? 'Ad offer · applied in cart' : 'Calculating your local offer price…');
      getQuote({
        country, currency, variantId: state.rule.variantId, code: state.rule.code,
        nativePriceCents: variant.price, targetPriceCents: state.rule.targetPriceCents,
      }, forceQuote).then((quote) => {
        if (requestGeneration !== generation || !product.isConnected) return;
        if (!eligibility(variant).ok) { applyVariant(variant); return; }
        state.status = 'ready';
        renderPrice(quote.priceCents);
        renderState('Ad offer · applied in cart');
      }).catch(() => {
        if (requestGeneration !== generation || !product.isConnected) return;
        restorePrice();
        state.status = 'quote-unavailable';
        renderState('Your offer will be verified in cart.');
      });
    };

    const selectedVariant = (form) => {
      const value = (form || product).querySelector('[data-product-form-variant]')?.value;
      return variants.find((variant) => String(variant.id) === String(value)) || null;
    };

    product.addEventListener('starmist:variant-change', (event) => {
      if (event.target !== product) return;
      const variant = variants.find((entry) => String(entry.id) === String(event.detail?.variant?.id));
      if (variant) applyVariant(variant);
    });
    product.querySelectorAll('[data-selling-plan-select]').forEach((select) => {
      select.addEventListener('change', () => applyVariant(selectedVariant()));
    });
    forms.forEach((form) => {
      form.addEventListener('submit', () => {
        const variant = selectedVariant(form);
        if (!eligibility(variant).ok || String(variant?.id) !== String(state.variant?.id)) applyVariant(variant);
        if (!state.active) return;
        const updated = core.selectProductOffer(config, {
          productId,
          search: `?offer=${encodeURIComponent(state.offer.key)}`,
          storedSelections: readSelections(),
          marketId,
          now: Date.now(),
        });
        saveSelections(updated.record);
        // Observe only: native form/OCU keeps ownership of submission/navigation.
      }, { capture: true });
    });
    refreshProducts.set(product, () => {
      refreshSelection();
      applyVariant(selectedVariant(), true);
    });
    applyVariant(selectedVariant());
  };

  const init = (scope = document) => {
    const raw = parseJson(document.querySelector('script[data-product-offer-config]')?.textContent);
    const config = core.normalizeConfig(raw);
    if (!config) return;
    const products = Array.from(scope.querySelectorAll(PRODUCT_SELECTOR));
    if (scope.matches?.(PRODUCT_SELECTOR)) products.unshift(scope);
    products.forEach((product) => setupProduct(product, config, raw));
  };

  window.StarmistProductOfferDisplay = Object.freeze({ init, validateQuote });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll(PRODUCT_SELECTOR).forEach((product) => refreshProducts.get(product)?.());
  });
})();
